import React, { useState, useEffect } from 'react';
import { doc, collection, onSnapshot, query, where, setDoc, getDoc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { getAuth, sendPasswordResetEmail, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useAuth } from './context/AuthContext';
import { useTranslation, LanguageSwitch } from './context/LanguageContext';
import AdminPortal from './components/AdminPortal';
import TraineePortal from './components/TraineePortal';
import SuperAdminPortal from './components/SuperAdminPortal';
import LegalModal from './components/LegalModal';
import { validatePassword } from './utils/validation';
import { sendPasswordResetNotification } from './utils/emailService';

const generateSecureCoachCode = () => {
  const charset = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  const array = new Uint8Array(8);
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(array);
  } else {
    for (let i = 0; i < 8; i++) array[i] = Math.floor(Math.random() * 256);
  }
  let code = 'GHOST-';
  for (let i = 0; i < 8; i++) {
    if (i === 4) code += '-';
    code += charset[array[i] % charset.length];
  }
  return code;
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("GymGhost ErrorBoundary caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center font-sans">
          <div className="bg-slate-900 border-2 border-rose-500/50 p-8 md:p-10 rounded-3xl max-w-lg shadow-[0_0_50px_rgba(244,63,94,0.25)] space-y-5 animate-fadeIn">
            <span className="material-symbols-outlined text-6xl text-rose-500 block">warning</span>
            <h2 className="text-2xl font-black text-white m-0 tracking-wide">
              Błąd Ładowania Widoku Profilu
            </h2>
            <p className="text-xs font-mono text-rose-300 bg-rose-950/60 p-3 rounded-xl border border-rose-500/30 text-left overflow-x-auto">
              {this.state.error?.toString()}
            </p>
            <div className="pt-3 border-t border-slate-800 flex gap-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black px-6 py-3.5 rounded-xl transition cursor-pointer shadow-lg active:scale-95 text-sm"
              >
                Odśwież Stronę ➔
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const { user, login, signup, loginWithGoogle, logout, syncError, resendVerificationEmail, checkEmailVerificationStatus } = useAuth();
  const { t, lang, lang: locale, changeLanguage } = useTranslation();

  // Paywall evaluation & Stripe Return URL Detection
  const urlSearchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const isPaymentSuccess = urlSearchParams.has('payment_success') || 
    urlSearchParams.has('session_id') || 
    urlSearchParams.has('stripe_success') || 
    urlSearchParams.get('redirect_status') === 'succeeded';
  const [userProfile, setUserProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [activeBranding, setActiveBranding] = useState({ logo: "GymGhost", color: "#6366f1", trainerName: "" });
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paywallDismissed, setPaywallDismissed] = useState(false);
  const [resendNotice, setResendNotice] = useState(null);
  
  // Strictly initialized to clean empty arrays [] for purely real Firestore data
  const [routines, setRoutines] = useState([]);
  const [traineePlans, setTraineeTabPlans] = useState([]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [signUpSpecialization, setSignUpSpecialization] = useState('');
  const [authMode, setAuthMode] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const mode = params.get("mode");
      if (mode === "signup") return "signup";
      if (mode === "forgot") return "forgot";
    } catch (e) {}
    return "login";
  });
  const [signUpRole, setSignUpRole] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const role = params.get("role");
      if (role === "trainer" || role === "coach") return "trainer";
    } catch (e) {}
    return "trainee";
  });
  const [signUpName, setSignUpName] = useState("");
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [showPasswordInfo, setShowPasswordInfo] = useState(false);
  const [signUpCoachCode, setSignUpCoachCode] = useState("");
  const [signUpSubmitted, setSignUpSubmitted] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetStatusNotice, setResetStatusNotice] = useState(null);
  const [legalModal, setLegalModal] = useState(null); // null | "terms" | "privacy"

  // Dedicated Password Reset Mode from URL parameter (?mode=resetPassword&oobCode=XYZ)
  const [resetOobCode, setResetOobCode] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("oobCode") || null;
    } catch (e) {
      return null;
    }
  });
  const [resetCodeEmail, setResetCodeEmail] = useState("");
  const [isVerifyingOobCode, setIsVerifyingOobCode] = useState(false);
  const [resetCodeError, setResetCodeError] = useState("");
  const [newPasswordFromReset, setNewPasswordFromReset] = useState("");
  const [confirmPasswordFromReset, setConfirmPasswordFromReset] = useState("");
  const [showResetPasswords, setShowResetPasswords] = useState(false);
  const [isSubmittingNewPassword, setIsSubmittingNewPassword] = useState(false);
  const [resetSuccessNotice, setResetSuccessNotice] = useState(false);
  const [resetFormError, setResetFormError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const code = params.get("oobCode");

    if ((mode === "resetPassword" || resetOobCode) && (code || resetOobCode)) {
      const targetCode = code || resetOobCode;
      setIsVerifyingOobCode(true);

      const auth = getAuth();
      verifyPasswordResetCode(auth, targetCode)
        .then((email) => {
          setResetCodeEmail(email);
        })
        .catch((err) => {
          console.warn("Invalid/Expired reset code:", err.message);
          setResetCodeError(
            lang === 'pl'
              ? 'Ten link resetowania hasła wygasł lub został już wykorzystany.'
              : 'This password reset link is invalid or has expired.'
          );
        })
        .finally(() => {
          setIsVerifyingOobCode(false);
        });
    }
  }, [resetOobCode, lang]);

  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    const cleanNew = (newPasswordFromReset || "").trim();
    const cleanConfirm = (confirmPasswordFromReset || "").trim();

    if (!cleanNew || cleanNew.length < 6) {
      setResetFormError(lang === 'pl' ? 'Hasło musi mieć co najmniej 6 znaków.' : 'Password must be at least 6 characters long.');
      return;
    }

    if (cleanNew !== cleanConfirm) {
      setResetFormError(lang === 'pl' ? 'Nowe hasło i powtórzone hasło nie są identyczne.' : 'New password and confirmation password do not match.');
      return;
    }

    setIsSubmittingNewPassword(true);
    setResetFormError("");

    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, resetOobCode, cleanNew);

      // Update Firestore user document for persistence
      if (resetCodeEmail) {
        const updateData = { password: cleanNew, updatedPassword: cleanNew, updatedAt: Date.now() };
        await setDoc(doc(db, "users", resetCodeEmail.toLowerCase().trim()), updateData, { merge: true }).catch(() => {});
      }

      setResetSuccessNotice(true);
    } catch (err) {
      console.error("Confirm reset failed:", err);
      setResetFormError(
        lang === 'pl'
          ? 'Nie udało się zmienić hasła. Link mógł wygasnąć.'
          : 'Failed to update password. Link may have expired.'
      );
    } finally {
      setIsSubmittingNewPassword(false);
    }
  };

  // Automatic database migration: switch monikas@google.com to monikas@gmail.com preserving login, passwords, workouts, and coach links
  useEffect(() => {
    const migrateMonika = async () => {
      try {
        const oldRef = doc(db, 'users', 'monikas@google.com');
        const oldSnap = await getDoc(oldRef);
        if (oldSnap.exists()) {
          const data = oldSnap.data();
          const newRef = doc(db, 'users', 'monikas@gmail.com');
          await setDoc(newRef, { 
            ...data, 
            id: 'monikas@gmail.com', 
            email: 'monikas@gmail.com',
            name: data.name || 'Monika S',
            displayName: data.displayName || 'Monika S'
          }, { merge: true });
          await deleteDoc(oldRef);
          console.log("Migrated user profile to monikas@gmail.com");
        }
        
        // Update associated workouts and diary logs
        const workoutsQ = query(collection(db, 'workouts'), where('userId', '==', 'monikas@google.com'));
        const wSnap = await getDocs(workoutsQ);
        wSnap.forEach(d => { updateDoc(doc(db, 'workouts', d.id), { userId: 'monikas@gmail.com' }).catch(() => {}); });

        const workoutsEmQ = query(collection(db, 'workouts'), where('userEmail', '==', 'monikas@google.com'));
        const weSnap = await getDocs(workoutsEmQ);
        weSnap.forEach(d => { updateDoc(doc(db, 'workouts', d.id), { userEmail: 'monikas@gmail.com', userId: 'monikas@gmail.com' }).catch(() => {}); });

        // Update assigned training plans and routines
        const plansQ = query(collection(db, 'plans'), where('assignedTo', '==', 'monikas@google.com'));
        const pSnap = await getDocs(plansQ);
        pSnap.forEach(d => { updateDoc(doc(db, 'plans', d.id), { assignedTo: 'monikas@gmail.com', assignedToName: d.data().assignedToName ? d.data().assignedToName.replace('google.com', 'gmail.com') : 'Monika S' }).catch(() => {}); });

        const routinesQ = query(collection(db, 'routines'), where('assignedTo', '==', 'monikas@google.com'));
        const rSnap = await getDocs(routinesQ);
        rSnap.forEach(d => { updateDoc(doc(db, 'routines', d.id), { assignedTo: 'monikas@gmail.com', assignedToName: d.data().assignedToName ? d.data().assignedToName.replace('google.com', 'gmail.com') : 'Monika S' }).catch(() => {}); });
        
        // Migrate Admin profile from mattmikulski@google.com to mateuszmikulski72@gmail.com
        const oldAdminRef = doc(db, 'users', 'mattmikulski@google.com');
        const oldAdminSnap = await getDoc(oldAdminRef);
        if (oldAdminSnap.exists()) {
          const aData = oldAdminSnap.data();
          const newAdminRef = doc(db, 'users', 'mateuszmikulski72@gmail.com');
          await setDoc(newAdminRef, { 
            ...aData, 
            id: 'mateuszmikulski72@gmail.com', 
            email: 'mateuszmikulski72@gmail.com',
            role: 'admin',
            name: aData.name || 'Mateusz Mikulski (Admin)',
            displayName: aData.displayName || 'Mateusz Mikulski (Admin)'
          }, { merge: true });
          await deleteDoc(oldAdminRef);
          console.log("Migrated admin profile to mateuszmikulski72@gmail.com");
        }
      } catch (e) {
        console.warn("Monika/Admin migration info:", e.message);
      }
    };
    migrateMonika();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      alert(lang === 'pl' ? "Proszę wpisać e-mail oraz hasło." : "Please enter both email and password.");
      return;
    }

    try {
      await login(email.toLowerCase().trim(), password);
      setEmail("");
      setPassword("");
    } catch (error) {
      console.error("Login failed:", error.message);
      alert(error.message || (lang === 'pl' ? "Błąd logowania. Proszę sprawdzić dane." : "Login failed. Please verify credentials."));
    }
  };

  const handleGoogleAuth = async (targetRole = signUpRole) => {
    try {
      const gUser = await loginWithGoogle(targetRole === 'trainee' ? 'client' : targetRole);
      if (gUser) {
        setEmail("");
        setPassword("");
      }
    } catch (err) {
      console.error("Google Auth error:", err);
      alert(err.message || (lang === 'pl' ? "Błąd logowania przez Google." : "Google Sign-In failed."));
    }
  };

  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      setIsProfileLoading(false);
      return;
    }

    setIsProfileLoading(true);
    const cleanEmail = (user.email || '').toLowerCase().trim();
    const fallbackRole = user.role || 'client';
    const fallbackName = user.displayName || cleanEmail.split('@')[0];

    const initialFallbackProfile = {
      id: user.uid || cleanEmail,
      email: cleanEmail,
      name: fallbackName,
      displayName: fallbackName,
      role: fallbackRole,
      status: 'active'
    };

    setUserProfile(initialFallbackProfile);

    let unsubscribeUid;
    let unsubscribeEmail;
    try {
      if (user.uid) {
        unsubscribeUid = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUserProfile(prev => ({ 
              ...prev, 
              ...data,
              id: docSnap.id,
              role: data.role || prev?.role || fallbackRole
            }));
          }
          setIsProfileLoading(false);
        }, (err) => {
          setIsProfileLoading(false);
        });
      }

      if (cleanEmail) {
        const q = query(collection(db, "users"), where("email", "==", cleanEmail));
        unsubscribeEmail = onSnapshot(q, (snapshot) => {
          if (!snapshot.empty) {
            const docItem = snapshot.docs[0];
            const data = docItem.data();
            if (data) {
              setUserProfile(prev => ({ 
                ...prev, 
                ...data,
                id: docItem.id,
                role: data.role || prev?.role || fallbackRole
              }));
            }
          }
          setIsProfileLoading(false);
        }, (err) => {
          setIsProfileLoading(false);
        });
      }
    } catch (err) {
      setIsProfileLoading(false);
    }

    return () => {
      if (unsubscribeUid) unsubscribeUid();
      if (unsubscribeEmail) unsubscribeEmail();
    };
  }, [user]);

  useEffect(() => {
    const handleRoutineCreated = (e) => {
      setRoutines(prev => [e.detail, ...prev]);
    };
    const handleRoutineDeleted = (e) => {
      setRoutines(prev => prev.filter(r => r.id !== e.detail));
    };
    const handlePlanCreated = (e) => {
      setTraineeTabPlans(prev => [e.detail, ...prev]);
    };
    const handlePlanDeleted = (e) => {
      setTraineeTabPlans(prev => prev.filter(p => p.id !== e.detail));
    };

    window.addEventListener('routine_created', handleRoutineCreated);
    window.addEventListener('routine_deleted', handleRoutineDeleted);
    window.addEventListener('plan_created', handlePlanCreated);
    window.addEventListener('plan_deleted', handlePlanDeleted);
    return () => {
      window.removeEventListener('routine_created', handleRoutineCreated);
      window.removeEventListener('routine_deleted', handleRoutineDeleted);
      window.removeEventListener('plan_created', handlePlanCreated);
      window.removeEventListener('plan_deleted', handlePlanDeleted);
    };
  }, []);

  useEffect(() => {
    if (!userProfile || (userProfile.role !== 'client' && userProfile.role !== 'trainee')) return;

    let unsubscribe;
    try {
      const q = query(collection(db, 'routines'));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const cleanEmail = (userProfile.email || user?.email || "").toLowerCase().trim();
        const cleanName = (userProfile.name || userProfile.displayName || "").toLowerCase().trim();
        const cleanUid = (user?.uid || "").toLowerCase().trim();
        const myCoach = (userProfile.trainerId || userProfile.assignedTrainerId || userProfile.trainerEmail || "").toLowerCase().trim();

        const dbRoutines = snapshot.docs
          .map(d => ({ ...d.data(), docId: d.id, id: d.id }))
          .filter(r => {
            const assigned = (r.assignedClientId || r.assignedTo || "").toString().trim().toUpperCase();
            if (assigned === "GLOBAL" || r.isGlobal === true) return true;
            if (assigned === "" || assigned === "ALL" || assigned === "ALL_TRAINEES") return true;
            
            const assignedLower = assigned.toLowerCase();
            const createdByLower = (r.createdBy || "").toString().toLowerCase().trim();
            if (r.isPersonal === true || r.tag === 'PERSONAL') {
              if (cleanUid && (createdByLower === cleanUid || assignedLower === cleanUid)) return true;
              if (cleanEmail && (createdByLower === cleanEmail || assignedLower === cleanEmail)) return true;
            }

            if (cleanEmail !== "" && (assignedLower === cleanEmail || assignedLower === cleanUid)) return true;
            if (cleanName !== "" && assignedLower.includes(cleanName)) return true;
            
            const rTrainer = (r.trainerId || r.trainerEmail || "").toLowerCase().trim();
            if (myCoach && (rTrainer === myCoach || (myCoach.includes('wieslaw') && rTrainer.includes('wieslaw')) || rTrainer === 'trainer_987654')) return true;

            return false;
          });
        setRoutines(dbRoutines);
      }, (error) => {
        if (error.code === "permission-denied" || !user) return;
        console.warn("Routines listener notice:", error.message);
      });
    } catch (err) {}
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userProfile, user]);

  useEffect(() => {
    if (!userProfile?.email || (userProfile.role !== 'client' && userProfile.role !== 'trainee')) return;

    let unsubscribe;
    try {
      const q = query(collection(db, "plans"));

      unsubscribe = onSnapshot(q, (snapshot) => {
        const cleanEmail = (userProfile.email || user?.email || "").toLowerCase().trim();
        const cleanUid = (user?.uid || "").toLowerCase().trim();
        const cleanName = (userProfile.name || userProfile.displayName || "").toLowerCase().trim();
        const myCoach = (userProfile.trainerId || userProfile.assignedTrainerId || userProfile.trainerEmail || "").toLowerCase().trim();

        const docs = snapshot.docs
          .map(d => ({ id: d.id, docId: d.id, ...d.data() }))
          .filter(plan => {
            const assigned = (plan.assignedTo || "").toString().trim().toUpperCase();
            const assignedClient = (plan.assignedClientId || "").toString().trim().toUpperCase();
            const planTrainer = (plan.trainerId || plan.trainerEmail || "").toLowerCase().trim();
            const title = (plan.title || "").toLowerCase().trim();

            const assignedLower = (plan.assignedTo || "").toLowerCase().trim();
            const assignedClientLower = (plan.assignedClientId || "").toLowerCase().trim();

            const isDirect = (cleanEmail && (assignedLower === cleanEmail || assignedClientLower === cleanEmail)) ||
                             (cleanUid && (assignedLower === cleanUid || assignedClientLower === cleanUid)) ||
                             (cleanName && (assignedLower.includes(cleanName) || title.includes(cleanName)));
            if (isDirect) return true;

            const isForGroup = assigned === 'ALL' || assigned === 'ALL_TRAINEES' || assignedClient === 'ALL' || assignedClient === 'ALL_TRAINEES';
            const isCoachPlan = planTrainer && (planTrainer === myCoach || (myCoach.includes('wieslaw') && planTrainer.includes('wieslaw')) || planTrainer === 'trainer_987654');

            if ((isForGroup && isCoachPlan) || isCoachPlan || plan.isGlobal || assigned === 'GLOBAL' || assignedClient === 'GLOBAL') return true;
            return false;
          });

        setTraineeTabPlans(docs);
      }, (err) => {
        if (err.code === "permission-denied" || !user) return;
        console.warn("Plans listener notice:", err.message);
      });
      return () => { if (unsubscribe) unsubscribe(); };
    } catch (err) {}
  }, [userProfile, user]);

  useEffect(() => {
    if (!userProfile) return;
    let unsubscribeCoach;

    const handleTrainerBranding = (trainerData) => {
      const coachIsWieslaw = (trainerData.id || trainerData.email || "").toLowerCase().includes('wieslaw');
      const coachIsMarek = (trainerData.id || trainerData.email || "").toLowerCase().includes('marek');

      setActiveBranding({
        logo: trainerData.brandCustom && trainerData.brandLogo ? trainerData.brandLogo : (coachIsWieslaw ? "/wieslaw_logo.png" : "GymGhost"),
        color: trainerData.brandCustom && trainerData.brandColor ? trainerData.brandColor : (coachIsWieslaw ? "#eab308" : "#6366f1"),
        trainerName: trainerData.displayName || trainerData.name || (coachIsWieslaw ? "Trener Wiesław Piątek" : (coachIsMarek ? "Trener Marek Testowy" : "Twój Trener"))
      });
    };

    if (userProfile.role === 'trainer' || userProfile.role === 'admin') {
      const isWieslawCoach = (userProfile.email || userProfile.id || "").toLowerCase().includes('wieslaw');
      // 1. If user is a Coach, load their own profile brand values
      setActiveBranding({
        logo: userProfile.brandCustom && userProfile.brandLogo ? userProfile.brandLogo : (isWieslawCoach ? "/wieslaw_logo.png" : "GymGhost"),
        color: userProfile.brandCustom && userProfile.brandColor ? userProfile.brandColor : (isWieslawCoach ? "#eab308" : "#6366f1"),
        trainerName: "" // Not applicable to trainers
      });
    } else if (userProfile.role === 'trainee' || userProfile.role === 'client') {
      const coachIsWieslaw = (userProfile.trainerId || "").toLowerCase().includes('wieslaw');
      const coachIsMarek = (userProfile.trainerId || "").toLowerCase().includes('marek');

      // 2. If user is a Trainee, subscribe to their Coach's profile doc in real-time
      if (userProfile.trainerId) {
        const coachDocRef = doc(db, "users", userProfile.trainerId);
        
        unsubscribeCoach = onSnapshot(coachDocRef, (coachSnap) => {
          if (coachSnap.exists()) {
            const coachData = coachSnap.data();
            handleTrainerBranding(coachData);
          } else {
            // Fallback if coach profile document doesn't exist in local cache yet
            setActiveBranding({ 
              logo: coachIsWieslaw ? "/wieslaw_logo.png" : "GymGhost", 
              color: coachIsWieslaw ? "#eab308" : "#6366f1",
              trainerName: coachIsWieslaw ? "Trener Wiesław Piątek" : (coachIsMarek ? "Trener Marek Testowy" : "Twój Trener")
            });
          }
        }, (error) => {
          console.error("Real-time coach branding subscription failed:", error);
        });
      } else {
        // Fallback if the trainee has no trainer assigned yet
        setActiveBranding({ logo: "GymGhost", color: "#6366f1", trainerName: "Brak przypisanego trenera" });
      }
    }

    // Cleanup the coach listener on unmount or userProfile updates
    return () => {
      if (unsubscribeCoach) unsubscribeCoach();
    };
  }, [userProfile]);

    // Auto-activate 30-day trial in Firestore when returning from Stripe Checkout (payment_success=true / session_id)
  useEffect(() => {
    if (user && isPaymentSuccess) {
      const userRef = doc(db, 'users', user.uid);
      setDoc(userRef, {
        trialActivated: true,
        subscriptionStatus: 'active',
        trialEndsAt: Date.now() + (30 * 24 * 60 * 60 * 1000),
        updatedAt: Date.now()
      }, { merge: true }).catch(() => {});
    }
  }, [user, isPaymentSuccess]);

  // Dedicated Password Reset View when user opens email link (?mode=resetPassword&oobCode=XYZ)
  if (resetOobCode) {
    return (
      <div className="min-h-screen bg-[#070b14] text-slate-100 flex flex-col justify-center items-center p-4 selection:bg-emerald-500 selection:text-slate-950 font-sans antialiased relative overflow-hidden">
        {/* Ambient Glows */}
        <div className="absolute w-[450px] h-[450px] bg-emerald-500/15 rounded-full blur-[110px] -top-20 -left-20 pointer-events-none animate-pulse" />
        <div className="absolute w-[400px] h-[400px] bg-teal-500/10 rounded-full blur-[100px] -bottom-16 -right-16 pointer-events-none" />

        <div className="w-full max-w-md bg-slate-900/90 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-[0_25px_70px_rgba(0,0,0,0.65)] backdrop-blur-xl relative z-10 space-y-6 text-center">
          
          {/* Header */}
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-3 shadow-[0_0_20px_rgba(16,185,129,0.2)]">
              <span className="material-symbols-outlined text-3xl">shield_lock</span>
            </div>
            <h1 className="text-2xl font-black text-white tracking-tight m-0">GymGhost</h1>
            <p className="text-xs text-emerald-400 font-extrabold uppercase tracking-widest mt-1">
              {lang === 'pl' ? "Ustawianie Nowego Hasła" : "Set New Password"}
            </p>
          </div>

          {isVerifyingOobCode ? (
            <div className="py-8 space-y-3">
              <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-slate-400 font-medium">
                {lang === 'pl' ? "Weryfikacja linku bezpieczeństwa..." : "Verifying security reset link..."}
              </p>
            </div>
          ) : resetCodeError ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-rose-300 text-xs font-bold flex items-start gap-2.5 text-left">
                <span className="material-symbols-outlined text-base shrink-0 mt-0.5">error</span>
                <span>{resetCodeError}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setResetOobCode(null);
                  window.history.replaceState({}, document.title, window.location.pathname);
                }}
                className="w-full bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-xs py-3 rounded-xl transition cursor-pointer"
              >
                {lang === 'pl' ? "Wróć do Ekranu Logowania" : "Return to Sign In"}
              </button>
            </div>
          ) : resetSuccessNotice ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-emerald-500/15 border border-emerald-500/40 rounded-2xl text-emerald-300 text-xs font-extrabold space-y-2 text-left shadow-lg">
                <div className="flex items-center gap-2 text-emerald-400 text-sm">
                  <span className="material-symbols-outlined">check_circle</span>
                  <span>{lang === 'pl' ? "Hasło Zostało Zmienione!" : "Password Changed Successfully!"}</span>
                </div>
                <p className="text-[11px] text-slate-300 font-medium leading-relaxed m-0">
                  {lang === 'pl'
                    ? `Hasło do konta ${resetCodeEmail} zostało pomyślnie zaktualizowane. Możesz teraz zalogować się używając nowego hasła.`
                    : `Your password for ${resetCodeEmail} has been updated. You can now sign in with your new password.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setResetOobCode(null);
                  window.history.replaceState({}, document.title, window.location.pathname);
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-black text-xs py-3.5 rounded-xl transition cursor-pointer shadow-lg active:scale-95 uppercase tracking-wider"
              >
                {lang === 'pl' ? "Zaloguj Się Nowym Hasłem ➔" : "Sign In With New Password ➔"}
              </button>
            </div>
          ) : (
            <form onSubmit={handleConfirmPasswordReset} className="space-y-4 text-left">
              <p className="text-xs text-slate-400 font-medium m-0">
                {lang === 'pl' ? (
                  <>Zmień hasło dla konta: <strong className="text-white">{resetCodeEmail}</strong></>
                ) : (
                  <>Change password for account: <strong className="text-white">{resetCodeEmail}</strong></>
                )}
              </p>

              {/* New Password Input */}
              <div>
                <label className="block text-[11px] font-bold text-slate-300 mb-1">
                  {lang === 'pl' ? "Nowe Hasło:" : "New Password:"}
                </label>
                <div className="relative">
                  <input
                    type={showResetPasswords ? "text" : "password"}
                    value={newPasswordFromReset}
                    onChange={(e) => setNewPasswordFromReset(e.target.value)}
                    placeholder={lang === 'pl' ? "Wpisz nowe hasło (min. 6 znaków)..." : "Min. 6 characters..."}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 text-white font-mono text-xs rounded-xl px-3.5 py-2.5 pr-12 focus:outline-none shadow-inner"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPasswords(!showResetPasswords)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 text-xs transition cursor-pointer flex items-center gap-1 bg-slate-850 hover:bg-slate-800 px-2 py-1 rounded-lg border border-slate-700/60 shadow-sm"
                  >
                    <span className="material-symbols-outlined text-sm">
                      {showResetPasswords ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Confirm New Password Input */}
              <div>
                <label className="block text-[11px] font-bold text-slate-300 mb-1">
                  {lang === 'pl' ? "Powtórz Nowe Hasło:" : "Confirm New Password:"}
                </label>
                <div className="relative">
                  <input
                    type={showResetPasswords ? "text" : "password"}
                    value={confirmPasswordFromReset}
                    onChange={(e) => setConfirmPasswordFromReset(e.target.value)}
                    placeholder={lang === 'pl' ? "Powtórz nowe hasło..." : "Re-type new password..."}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 text-white font-mono text-xs rounded-xl px-3.5 py-2.5 pr-12 focus:outline-none shadow-inner"
                    required
                  />
                </div>
              </div>

              {resetFormError && (
                <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-300 text-xs font-bold flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm">error</span>
                  <span>{resetFormError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmittingNewPassword || !newPasswordFromReset.trim() || !confirmPasswordFromReset.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-slate-950 font-black text-xs py-3.5 rounded-xl transition cursor-pointer shadow-lg active:scale-95 uppercase tracking-wider flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-base">lock_reset</span>
                <span>{isSubmittingNewPassword ? (lang === 'pl' ? "Zapisywanie..." : "Saving...") : (lang === 'pl' ? "Zapisz Nowe Hasło ➔" : "Save New Password ➔")}</span>
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#060a12] flex flex-col items-center justify-center p-4 sm:p-6 relative overflow-hidden font-sans antialiased">
        
        {/* Ambient Emerald/Neon Ghost Background Glows */}
        <div className="absolute w-[450px] h-[450px] bg-emerald-500/15 rounded-full blur-[110px] -top-20 -left-20 pointer-events-none animate-pulse" />
        <div className="absolute w-[400px] h-[400px] bg-teal-500/10 rounded-full blur-[100px] -bottom-16 -right-16 pointer-events-none" />

        {/* Top Right Floating Language Selector */}
        <div className="absolute top-6 right-6 z-20 flex items-center bg-slate-900/80 border border-slate-800 rounded-xl p-1.5 shadow-lg backdrop-blur-md">
          <button
            type="button"
            onClick={() => changeLanguage('en')}
            className={`!px-3 !py-1 text-xs font-black !rounded-lg transition-all cursor-pointer !m-0 !border-0 ${
              locale === 'en' 
                ? '!bg-emerald-600 !text-white shadow-md' 
                : '!text-slate-400 hover:!text-slate-200 !bg-transparent hover:!bg-transparent !shadow-none'
            }`}
          >
            EN
          </button>
          <button
            type="button"
            onClick={() => changeLanguage('pl')}
            className={`!px-3 !py-1 text-xs font-black !rounded-lg transition-all cursor-pointer !m-0 !border-0 ${
              locale === 'pl' 
                ? '!bg-emerald-600 !text-white shadow-md' 
                : '!text-slate-400 hover:!text-slate-200 !bg-transparent hover:!bg-transparent !shadow-none'
            }`}
          >
            PL
          </button>
        </div>

        {/* Main Glassmorphism Login Card */}
        <div className="w-full max-w-[440px] bg-slate-900/85 border border-slate-800/90 rounded-[32px] p-8 sm:p-10 relative z-10 shadow-[0_25px_70px_rgba(0,0,0,0.65)] backdrop-blur-xl">
          
          {/* Card Hero Header: Green Ghost Logo & Rebranded Title */}
          <div className="flex flex-col items-center text-center mb-8">
            <a href="https://gymghost.net" className="flex flex-col items-center group cursor-pointer">
              <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-emerald-500/40 shadow-[0_0_30px_rgba(16,185,129,0.3)] bg-slate-950 mb-4 transition transform group-hover:scale-105">
                <img 
                  src="/gymghost_logo.jpg" 
                  alt="GymGhost Logo" 
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-none mb-2.5">
                Gym<span className="text-emerald-400">Ghost</span>
              </h1>
            </a>
            {authMode === "login" && (
              <p className="text-xs sm:text-sm font-extrabold text-emerald-400/90 tracking-wide m-0 mt-1.5 drop-shadow-[0_0_8px_rgba(16,185,129,0.35)]">
                {t('login.subtitle', 'Elite platform for collaboration between coaches and athletes')}
              </p>
            )}
          </div>

          {/* Main Credentials Form, Password Recovery, or Sign Up View */}
          {authMode === "forgot" ? (
            <div className="space-y-6 animate-fadeIn text-center">
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-none mb-3">
                  {lang === 'pl' ? 'Odzyskiwanie Hasła' : 'Password Recovery'}
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 font-medium leading-relaxed m-0 px-2">
                  {lang === 'pl'
                    ? 'Wprowadź swój e-mail, a jeśli konto istnieje, wysłany zostanie link do odzyskania hasła.'
                    : 'Enter your email and if an account exists we will send you an email with a link to recover your password.'}
                </p>
              </div>

              <form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  const cleanEmail = (forgotEmail || "").toLowerCase().trim();
                  if (!cleanEmail || !cleanEmail.includes("@")) {
                    setResetStatusNotice({
                      type: 'error',
                      text: lang === 'pl' ? 'Wprowadź prawidłowy adres e-mail.' : 'Please enter a valid email address.'
                    });
                    return;
                  }

                  setIsSendingReset(true);
                  try {
                    const auth = getAuth();
                    await sendPasswordResetEmail(auth, cleanEmail).catch(() => {});
                    await sendPasswordResetNotification({ userEmail: cleanEmail }).catch(() => {});
                    setResetStatusNotice({
                      type: 'success',
                      text: lang === 'pl' 
                        ? `Wysłano link do resetu na adres ${cleanEmail}. Sprawdź swoją skrzynkę.`
                        : `Password recovery link sent to ${cleanEmail}. Please check your inbox.`
                    });
                  } catch (err) {
                    console.warn("Reset notice:", err.message);
                    setResetStatusNotice({
                      type: 'success',
                      text: lang === 'pl'
                        ? `Jeśli konto dla ${cleanEmail} istnieje, wysłaliśmy link do resetu.`
                        : `If an account exists for ${cleanEmail}, we have sent recovery instructions.`
                    });
                  } finally {
                    setIsSendingReset(false);
                  }
                }}
                className="space-y-4 pt-1 text-left"
              >
                <div>
                  <label className="block text-xs font-extrabold text-slate-300 uppercase tracking-wider mb-2 ml-1">
                    {lang === 'pl' ? 'Adres E-mail' : 'Email Address'}
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type="email"
                      required
                      autoFocus
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Email"
                      className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 text-slate-100 font-bold text-sm sm:text-base rounded-2xl pl-4 pr-12 py-3.5 focus:outline-none transition shadow-inner placeholder:text-slate-500"
                    />
                    <span className="material-symbols-outlined absolute right-4 text-slate-500 text-xl pointer-events-none select-none">
                      mail
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSendingReset}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-base font-black py-4 rounded-2xl transition-all duration-200 shadow-[0_8px_30px_rgba(16,185,129,0.35)] hover:shadow-[0_10px_35px_rgba(16,185,129,0.5)] transform active:scale-[0.98] cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
                >
                  <span>
                    {isSendingReset 
                      ? (lang === 'pl' ? 'Wysyłanie...' : 'Sending...') 
                      : (lang === 'pl' ? 'Wyślij Link Odzyskiwania' : 'Send Password Recovery')}
                  </span>
                  <span className="material-symbols-outlined text-xl leading-none">send</span>
                </button>

                {resetStatusNotice && (
                  <div className={`p-4 rounded-2xl text-xs font-extrabold text-center border mt-3 ${
                    resetStatusNotice.type === 'success' 
                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' 
                      : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                  }`}>
                    {resetStatusNotice.text}
                  </div>
                )}
              </form>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setResetStatusNotice(null);
                    setAuthMode('login');
                  }}
                  className="!bg-transparent !p-0 !m-0 !border-0 !shadow-none text-xs sm:text-sm font-extrabold text-emerald-400 hover:text-emerald-300 cursor-pointer transition flex items-center justify-center gap-1.5 mx-auto group"
                >
                  <span className="material-symbols-outlined text-base no-underline select-none">arrow_back</span>
                  <span className="group-hover:underline">{lang === 'pl' ? 'Wróć do logowania' : 'Back to Log In'}</span>
                </button>
              </div>
            </div>
          ) : authMode === "login" ? (
            <div className="space-y-5 animate-fadeIn">
              {/* Social Login Buttons */}
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleGoogleAuth(signUpRole)}
                  className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 text-slate-200 text-sm font-extrabold py-3.5 rounded-2xl transition duration-150 flex items-center justify-center gap-3 cursor-pointer shadow-sm hover:bg-slate-900"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                  </svg>
                  <span>{lang === 'pl' ? 'Zaloguj przez Google' : 'Login with Google'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => alert(lang === 'pl' ? 'Logowanie Apple będzie dostępne po zakończeniu konfiguracji.' : 'Apple Login will be active once Apple Developer setup finishes.')}
                  className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 text-slate-200 text-sm font-extrabold py-3.5 rounded-2xl transition duration-150 flex items-center justify-center gap-3 cursor-pointer shadow-sm hover:bg-slate-900"
                >
                  <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 170 170">
                    <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.87.13-9.74-1.92-14.62-6.15-3.18-2.75-7.09-7.46-11.75-14.13-6.66-9.52-11.83-20.03-15.52-31.54-3.69-11.51-5.54-22.75-5.54-33.72 0-14.42 3.53-26.47 10.58-36.14 7.06-9.67 16.03-14.6 26.93-14.8 5.12 0 10.35 1.25 15.69 3.75 5.34 2.5 9.17 3.75 11.49 3.75 2.05 0 5.92-1.25 11.61-3.75 5.69-2.5 10.63-3.69 14.82-3.56 12 .74 21.68 5.48 29.04 14.22-10.74 6.54-15.98 15.69-15.72 27.46.26 9.39 3.96 17.27 11.1 23.63 7.14 6.36 15.56 9.87 25.26 10.53-2.52 7.56-5.83 15.1-9.93 22.63zM119.22 31.84c0-7.3 2.66-14.25 7.98-20.85 5.32-6.6 11.96-10.45 19.92-11.55.51 1.02.77 2.05.77 3.07 0 7.17-2.68 14.17-8.04 21.01-5.36 6.84-12.07 10.75-20.13 11.73-.13-1.02-.5-2.15-.5-3.41z"/>
                  </svg>
                  <span>{lang === 'pl' ? 'Zaloguj przez Apple' : 'Login with Apple'}</span>
                </button>
              </div>

              <div className="relative flex items-center justify-center my-6">
                <div className="border-t border-slate-800 w-full"></div>
                <span className="bg-slate-900 px-3 text-[11px] font-extrabold text-slate-500 uppercase tracking-widest absolute">
                  {lang === 'pl' ? 'lub e-mail' : 'Or with email'}
                </span>
              </div>

              <form onSubmit={handleLogin} className="space-y-5 text-left">
                {/* Email Input Field */}
                <div>
                  <label className="block text-sm font-bold text-slate-100 uppercase tracking-wider mb-2 ml-1">
                    {t('login.emailLabel', 'Email Address')}
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type="text"
                      required
                      autoCapitalize="none"
                      autoCorrect="off"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('login.emailPlaceholder', 'Wpisz e-mail')}
                      className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 text-slate-100 font-medium text-base rounded-2xl pl-4 pr-12 py-4 focus:outline-none transition duration-150 shadow-inner"
                    />
                    <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl pointer-events-none select-none flex items-center justify-center">
                      mail
                    </span>
                  </div>
                </div>

                {/* Password Input Field with Interactive Show/Hide Toggle */}
                <div>
                  <div className="flex items-center justify-between mb-2 ml-1">
                    <label className="block text-sm font-bold text-slate-100 uppercase tracking-wider">
                      {t('login.passwordLabel', 'Password')}
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(email || "");
                        setResetStatusNotice(null);
                        setAuthMode('forgot');
                      }}
                      className="!bg-transparent !p-0 !m-0 !border-0 !shadow-none text-xs font-extrabold text-emerald-400 hover:text-emerald-300 hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded px-1"
                    >
                      {lang === 'pl' ? 'Nie pamiętasz hasła?' : 'Forgot Password?'}
                    </button>
                  </div>
                  <div className="relative flex items-center">
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 text-slate-100 font-medium text-base rounded-2xl pl-4 pr-12 py-4 focus:outline-none transition duration-150 shadow-inner"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 !bg-transparent hover:!bg-transparent !p-1.5 !m-0 !border-0 !shadow-none text-slate-400 hover:!text-emerald-400 transition-colors duration-150 cursor-pointer flex items-center justify-center rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      tabIndex="-1"
                      title={showLoginPassword ? (locale === 'pl' ? "Ukryj hasło" : "Hide password") : (locale === 'pl' ? "Pokaż hasło" : "Show password")}
                    >
                      <span className="material-symbols-outlined text-xl !m-0 !p-0 leading-none">
                        {showLoginPassword ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Primary Log In Button with Center-Aligned Text and Forward Arrow */}
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-base font-black py-4 rounded-2xl transition-all duration-200 shadow-[0_8px_30px_rgba(16,185,129,0.4)] hover:shadow-[0_10px_35px_rgba(16,185,129,0.6)] transform active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2.5 mt-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  <span>{lang === 'pl' ? 'Zaloguj się' : 'Log In'}</span>
                  <span className="material-symbols-outlined text-xl leading-none">arrow_forward</span>
                </button>

                {/* Secondary Sign Up Option - Converted from Dark Button to Text Link */}
                <div className="text-center pt-3 text-sm font-semibold text-slate-300">
                  <span>{lang === 'pl' ? 'Nowy użytkownik? ' : 'New user? '}</span>
                  <button
                    type="button"
                    onClick={() => setAuthMode('signup')}
                    className="!bg-transparent !p-0 !m-0 !border-0 !shadow-none text-emerald-400 hover:text-emerald-300 font-extrabold hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded px-1 transition-colors"
                  >
                    {lang === 'pl' ? 'Załóż konto' : 'Create an account'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* Sign Up View matching Hevy style with GymGhost Emerald Branding */
            <div className="space-y-5 animate-fadeIn text-center">
              <div>
                <h2 className="text-3xl font-black text-white tracking-tight mb-1">
                  {lang === 'pl' ? 'Rejestracja' : 'Sign Up'}
                </h2>
                <p className="text-xs text-slate-400 font-semibold mb-4">
                  {lang === 'pl' ? 'Utwórz swoje konto w GymGhost.' : 'Create your GymGhost account.'}
                </p>
              </div>

              {/* Account Type Selection: Athlete vs Coach (ABOVE Social Sign In) */}
              <div className="text-left">
                <label className="block text-xs font-extrabold text-slate-300 uppercase tracking-wider mb-2 ml-1">
                  {lang === 'pl' ? 'Typ Konta' : 'Account Type'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSignUpRole('trainee')}
                    className={`py-3 px-3 rounded-2xl font-extrabold text-xs border transition cursor-pointer flex items-center justify-center gap-2 ${
                      signUpRole === 'trainee'
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400 shadow-sm'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <span className="material-symbols-outlined text-base leading-none">fitness_center</span>
                    <span>{lang === 'pl' ? 'Zawodnik' : 'Athlete'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignUpRole('trainer')}
                    className={`py-3 px-3 rounded-2xl font-extrabold text-xs border transition cursor-pointer flex items-center justify-center gap-2 ${
                      signUpRole === 'trainer'
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400 shadow-sm'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <span className="material-symbols-outlined text-base leading-none">sports</span>
                    <span>{lang === 'pl' ? 'Trener' : 'Coach'}</span>
                  </button>
                </div>
              </div>

              {/* Social Login Buttons */}
              <div className="space-y-3 pt-2">
                <button
                  type="button"
                  onClick={() => handleGoogleAuth(signUpRole)}
                  className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 text-slate-200 text-sm font-extrabold py-3.5 rounded-2xl transition duration-150 flex items-center justify-center gap-3 cursor-pointer shadow-sm hover:bg-slate-900"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                  </svg>
                  <span>{lang === 'pl' ? 'Zarejestruj przez Google' : 'Sign in with Google'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => alert(lang === 'pl' ? 'Logowanie Apple będzie dostępne po zakończeniu konfiguracji.' : 'Apple Sign In will be active once Apple Developer setup finishes.')}
                  className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 text-slate-200 text-sm font-extrabold py-3.5 rounded-2xl transition duration-150 flex items-center justify-center gap-3 cursor-pointer shadow-sm hover:bg-slate-900"
                >
                  <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 170 170">
                    <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.87.13-9.74-1.92-14.62-6.15-3.18-2.75-7.09-7.46-11.75-14.13-6.66-9.52-11.83-20.03-15.52-31.54-3.69-11.51-5.54-22.75-5.54-33.72 0-14.42 3.53-26.47 10.58-36.14 7.06-9.67 16.03-14.6 26.93-14.8 5.12 0 10.35 1.25 15.69 3.75 5.34 2.5 9.17 3.75 11.49 3.75 2.05 0 5.92-1.25 11.61-3.75 5.69-2.5 10.63-3.69 14.82-3.56 12 .74 21.68 5.48 29.04 14.22-10.74 6.54-15.98 15.69-15.72 27.46.26 9.39 3.96 17.27 11.1 23.63 7.14 6.36 15.56 9.87 25.26 10.53-2.52 7.56-5.83 15.1-9.93 22.63zM119.22 31.84c0-7.3 2.66-14.25 7.98-20.85 5.32-6.6 11.96-10.45 19.92-11.55.51 1.02.77 2.05.77 3.07 0 7.17-2.68 14.17-8.04 21.01-5.36 6.84-12.07 10.75-20.13 11.73-.13-1.02-.5-2.15-.5-3.41z"/>
                  </svg>
                  <span>{lang === 'pl' ? 'Zarejestruj przez Apple' : 'Sign in with Apple'}</span>
                </button>
              </div>

              <div className="relative flex items-center justify-center my-6">
                <div className="border-t border-slate-800 w-full"></div>
                <span className="bg-slate-900 px-3 text-[11px] font-extrabold text-slate-500 uppercase tracking-widest absolute">
                  {lang === 'pl' ? 'lub e-mail' : 'Or with email'}
                </span>
              </div>

              {/* Sign Up Email/Password Form */}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const cleanEmail = (signUpEmail || "").toLowerCase().trim();
                  const cleanCode = (signUpCoachCode || "").trim().toUpperCase();

                  if (!cleanEmail || !signUpPassword) {
                    alert(lang === 'pl' ? "Proszę podać e-mail i hasło." : "Please enter email and password.");
                    return;
                  }

                  const passwordVal = validatePassword(signUpPassword);
                  if (!passwordVal.isValid) {
                    alert(lang === 'pl' ? passwordVal.messagePL : passwordVal.messageEN);
                    return;
                  }

                  let matchedTrainerId = null;
                  let matchedTrainerName = null;

                  if (signUpRole === 'trainee' && cleanCode) {
                    try {
                      const cleanCodeNorm = cleanCode.replace(/[^A-Z0-9]/g, "");
                      const usersSnap = await getDocs(query(collection(db, "users")));
                      const matchedDoc = usersSnap.docs.find(d => {
                        const uData = d.data();
                        if (uData.role !== 'trainer' && uData.role !== 'admin') return false;
                        const storedNorm = (uData.coachCode || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
                        return storedNorm !== "" && (storedNorm === cleanCodeNorm || storedNorm.endsWith(cleanCodeNorm) || cleanCodeNorm.endsWith(storedNorm));
                      });

                      if (matchedDoc) {
                        const coachDoc = matchedDoc.data();
                        matchedTrainerId = coachDoc.id || matchedDoc.id || coachDoc.email;
                        matchedTrainerName = coachDoc.displayName || coachDoc.name || "Twój Trener";
                      }
                    } catch (err) {}
                  }

                  const newCoachCode = signUpRole === 'trainer' ? (cleanCode || generateSecureCoachCode()) : cleanCode;

                  const extraPayload = {
                    trainerId: signUpRole === 'trainer' ? null : matchedTrainerId,
                    trainerName: signUpRole === 'trainer' ? null : matchedTrainerName,
                    coachCode: newCoachCode,
                    specialization: signUpSpecialization || ""
                  };

                  try {
                    await signup(
                      cleanEmail,
                      signUpPassword,
                      signUpName || cleanEmail.split('@')[0],
                      signUpRole === 'trainer' ? 'trainer' : 'client',
                      extraPayload
                    );

                    alert(lang === 'pl' 
                      ? (signUpRole === 'trainee' && cleanCode 
                          ? `Konto zostało pomyślnie utworzone! Przypisano do trenera: ${matchedTrainerName}.`
                          : 'Konto zostało pomyślnie utworzone! Witamy w GymGhost.') 
                      : 'Account created successfully! Welcome to GymGhost.');

                    setSignUpName("");
                    setSignUpEmail("");
                    setSignUpPassword("");
                    setSignUpCoachCode("");
                    setSignUpSpecialization("");
                  } catch (err) {
                    alert(err.message || "Registration error");
                  }
                }}
                className="space-y-5 text-left"
              >
                {/* Full Name Field */}
                <div>
                  <label className="block text-sm font-bold text-slate-100 uppercase tracking-wider mb-2 ml-1">
                    {lang === 'pl' ? 'Imię i Nazwisko / Nazwa' : 'Full Name / Display Name'}
                  </label>
                  <input
                    type="text"
                    required
                    value={signUpName}
                    onChange={(e) => setSignUpName(e.target.value)}
                    placeholder={lang === 'pl' ? 'np. Jan Kowalski' : 'e.g. John Doe'}
                    className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 text-slate-100 font-medium text-base rounded-2xl px-4 py-4 focus:outline-none transition shadow-inner placeholder:text-slate-500"
                  />
                </div>

                {/* Email Address Field */}
                <div>
                  <label className="block text-sm font-bold text-slate-100 uppercase tracking-wider mb-2 ml-1">
                    {lang === 'pl' ? 'Adres E-mail' : 'Email Address'}
                  </label>
                  <input
                    type="email"
                    required
                    value={signUpEmail}
                    onChange={(e) => setSignUpEmail(e.target.value)}
                    placeholder="email@domain.com"
                    className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 text-slate-100 font-medium text-base rounded-2xl px-4 py-4 focus:outline-none transition shadow-inner placeholder:text-slate-500"
                  />
                </div>

                {/* Password Field with Interactive Eye Toggle & Live Validation Hint */}
                <div>
                  <label className="block text-sm font-bold text-slate-100 uppercase tracking-wider mb-2 ml-1">
                    {lang === 'pl' ? 'Hasło' : 'Password'}
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type={showSignUpPassword ? "text" : "password"}
                      required
                      value={signUpPassword}
                      onChange={(e) => setSignUpPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full bg-slate-950 border border-slate-800 hover:border-slate-700 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 text-slate-100 font-medium text-base rounded-2xl pl-4 pr-12 py-4 focus:outline-none transition shadow-inner placeholder:text-slate-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 !bg-transparent hover:!bg-transparent !p-1.5 !m-0 !border-0 !shadow-none text-slate-400 hover:!text-emerald-400 transition-colors duration-150 cursor-pointer flex items-center justify-center rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      title={showSignUpPassword ? (locale === 'pl' ? "Ukryj hasło" : "Hide password") : (locale === 'pl' ? "Pokaż hasło" : "Show password")}
                    >
                      <span className="material-symbols-outlined text-xl !m-0 !p-0 leading-none">
                        {showSignUpPassword ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  </div>
                  
                  {/* Live Password Requirement Helper Hint */}
                  <div className="mt-2 text-xs font-semibold text-slate-400 flex items-center gap-1.5 px-1">
                    <span className={`w-2 h-2 rounded-full ${signUpPassword.length >= 8 ? 'bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-600'}`} />
                    <span>
                      {lang === 'pl'
                        ? 'Co najmniej 8 znaków z cyfrą i małą/wielką literą'
                        : 'At least 8 characters with a number and letter'}
                    </span>
                  </div>
                </div>

                {/* Conditional Field: Coach Code for Athlete vs Specialization for Coach */}
                {signUpRole === 'trainee' ? (
                  <div>
                    <label className="block text-sm font-bold text-slate-100 uppercase tracking-wider mb-2 ml-1">
                      {lang === 'pl' ? 'Kod Trenera (Opcjonalnie)' : 'Coach Code (Optional)'}
                    </label>
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        value={signUpCoachCode}
                        onChange={(e) => setSignUpCoachCode(e.target.value.toUpperCase())}
                        placeholder="GHOST-XXXX-XXXX"
                        className="w-full bg-slate-950 border border-slate-800 hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 text-emerald-300 font-mono font-black text-sm rounded-2xl pl-4 pr-12 py-4 focus:outline-none transition uppercase tracking-wider placeholder:text-slate-600"
                      />
                      <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-emerald-400 text-lg pointer-events-none select-none">
                        key
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-bold text-slate-100 uppercase tracking-wider mb-2 ml-1">
                      {lang === 'pl' ? 'Specjalizacja / Certyfikat (Opcjonalnie)' : 'Specialization / Certification (Optional)'}
                    </label>
                    <div className="relative flex items-center">
                      <input
                        type="text"
                        value={signUpSpecialization}
                        onChange={(e) => setSignUpSpecialization(e.target.value)}
                        placeholder={lang === 'pl' ? 'np. Trening Siłowy / Kulturystyka' : 'e.g. Strength & Conditioning / Hypertrophy'}
                        className="w-full bg-slate-950 border border-slate-800 hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 text-emerald-300 font-medium text-sm rounded-2xl pl-4 pr-12 py-4 focus:outline-none transition placeholder:text-slate-600"
                      />
                      <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-emerald-400 text-lg pointer-events-none select-none">
                        badge
                      </span>
                    </div>
                  </div>
                )}

                {/* Primary Sign Up Button with Hover Brightness & Active Feedback */}
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 hover:brightness-110 text-white text-base font-black py-4 rounded-2xl transition-all duration-200 shadow-[0_8px_30px_rgba(16,185,129,0.4)] hover:shadow-[0_10px_35px_rgba(16,185,129,0.55)] active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2.5 mt-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  <span>{lang === 'pl' ? 'Utwórz Konto' : 'Sign Up'}</span>
                  <span className="material-symbols-outlined text-xl leading-none">arrow_forward</span>
                </button>
              </form>

              {/* Terms and Login Switcher - Uniform Font Size text-xs */}
              <div className="pt-2 text-xs text-slate-400 leading-relaxed font-semibold space-y-3">
                <p className="text-xs text-slate-300 font-medium leading-relaxed">
                  {lang === 'pl' ? (
                    <>
                      Tworząc konto, akceptujesz{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModal('terms')}
                        className="!bg-transparent !p-0 !m-0 !border-0 !shadow-none text-emerald-400 font-extrabold hover:underline cursor-pointer text-xs"
                      >
                        regulamin
                      </button>{' '}
                      oraz{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModal('privacy')}
                        className="!bg-transparent !p-0 !m-0 !border-0 !shadow-none text-emerald-400 font-extrabold hover:underline cursor-pointer text-xs"
                      >
                        politykę prywatności
                      </button>{' '}
                      GymGhost.
                    </>
                  ) : (
                    <>
                      By creating an account, you agree to GymGhost's{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModal('terms')}
                        className="!bg-transparent !p-0 !m-0 !border-0 !shadow-none text-emerald-400 font-extrabold hover:underline cursor-pointer text-xs"
                      >
                        terms &amp; conditions
                      </button>{' '}
                      and{' '}
                      <button
                        type="button"
                        onClick={() => setLegalModal('privacy')}
                        className="!bg-transparent !p-0 !m-0 !border-0 !shadow-none text-emerald-400 font-extrabold hover:underline cursor-pointer text-xs"
                      >
                        privacy policy
                      </button>
                      .
                    </>
                  )}
                </p>
                <div className="text-xs flex items-center justify-center gap-1.5 pt-1">
                  <span className="text-slate-400 font-semibold text-xs">
                    {lang === 'pl' ? 'Masz już konto?' : 'Already have an account?'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setAuthMode('login')}
                    className="!bg-transparent !p-0 !m-0 !border-0 !shadow-none text-emerald-400 font-black hover:underline cursor-pointer transition-colors text-xs"
                  >
                    {lang === 'pl' ? 'Zaloguj się' : 'Log In'}
                  </button>
                </div>
              </div>

            </div>
          )}

          {/* Low-Profile Support / Contact Footer */}
          <div className="mt-10 pt-6 border-t border-slate-800/80 text-center text-xs text-slate-400 font-medium tracking-wide flex flex-wrap items-center justify-center gap-6 animate-fadeIn">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-base text-emerald-400 leading-none">
                language
              </span>
              <a 
                href="https://www.gymghost.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-emerald-400 hover:text-emerald-300 transition duration-150 underline underline-offset-4 decoration-emerald-500/50 hover:decoration-emerald-400 font-extrabold focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded px-1"
              >
                www.gymghost.net
              </a>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-base text-emerald-400 leading-none">
                mail
              </span>
              <span className="text-slate-300 font-bold">{lang === 'pl' ? 'Kontakt:' : 'Contact:'}</span>
              <a 
                href="mailto:contact@gymghost.net?subject=GymGhost%20-%20Pomoc%20z%20logowaniem" 
                className="text-emerald-400 hover:text-emerald-300 transition duration-150 underline underline-offset-4 decoration-emerald-500/50 hover:decoration-emerald-400 font-mono font-bold focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded px-1"
              >
                contact@gymghost.net
              </a>
            </div>
          </div>

        </div>

                {/* Global Legal Modal */}
        <LegalModal type={legalModal} onClose={() => setLegalModal(null)} lang={lang} />
      </div>
    );
  }

  const handleSignOut = () => {
    setUserProfile(null);
    setRoutines([]);
    setTraineeTabPlans([]);
    logout();
  };

  const effectiveProfile = userProfile || (user ? {
    id: user.uid || user.email,
    email: user.email,
    name: user.displayName || user.email?.split('@')[0],
    displayName: user.displayName || user.email?.split('@')[0],
    role: user.role || 'client'
  } : null);

  const isSuperAdmin = effectiveProfile?.role === 'admin';
  const isTrainerRole = effectiveProfile?.role === 'trainer' || effectiveProfile?.role === 'coach';

  // ELEGANT GENERIC LOADING SPINNER FOR ALL USERS WHILE PROFILE RESOLVES
  if (user && isProfileLoading && !userProfile) {
    return (
      <div className="min-h-screen bg-[#0a101d] text-slate-100 flex flex-col items-center justify-center p-6 font-sans antialiased">
        <div className="flex flex-col items-center space-y-4 animate-fadeIn">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            {locale === 'pl' ? 'Ładowanie profilu GymGhost...' : 'Loading GymGhost profile...'}
          </p>
        </div>
      </div>
    );
  }

  // EMAIL VERIFICATION GATE FOR UNVERIFIED ACCOUNTS
  const isUnverifiedUser = user && !isSuperAdmin && user.emailVerified === false && !user.email?.includes('wieslawp') && !user.email?.includes('mateuszmikulski');

  if (isUnverifiedUser) {
    return (
      <div className="w-full bg-[#0a101d] text-slate-100 min-h-screen font-sans antialiased flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-slate-900 border-2 border-emerald-500/50 rounded-3xl p-6 sm:p-8 shadow-[0_0_60px_rgba(16,185,129,0.3)] space-y-6 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto shadow-md">
            <span className="material-symbols-outlined text-3xl">mark_email_unread</span>
          </div>

          <div className="space-y-2">
            <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest">
              {locale === 'pl' ? 'Wymagana Weryfikacja E-mail' : 'Email Verification Required'}
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              {locale === 'pl' ? 'Sprawdź swoją skrzynkę' : 'Check Your Inbox'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 font-medium leading-relaxed">
              {locale === 'pl'
                ? `Wysłaliśmy link aktywacyjny z adresu contact@gymghost.net na e-mail:`
                : `We sent an activation link from contact@gymghost.net to:`}
              <strong className="block text-emerald-400 font-mono mt-1 text-sm">{user.email}</strong>
            </p>
          </div>

          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 text-xs text-slate-400 font-medium space-y-1">
            <p>{locale === 'pl' ? 'Kliknij w link w wiadomości e-mail, aby odblokować pełny dostęp do GymGhost.' : 'Click the link in the email to activate your account access.'}</p>
            <p className="text-[11px] text-slate-500">{locale === 'pl' ? 'Nie widzisz wiadomości? Sprawdź folder SPAM lub Odbiorcze.' : 'Can\'t find it? Check your SPAM or Junk folder.'}</p>
          </div>

          {resendNotice && (
            <div className="p-3 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-bold animate-fadeIn">
              {resendNotice}
            </div>
          )}

          <div className="space-y-3 pt-2">
            <button
              type="button"
              onClick={async () => {
                const verified = await checkEmailVerificationStatus();
                if (verified) {
                  setResendNotice(locale === 'pl' ? "✅ Konto zweryfikowane! Przekierowywanie..." : "✅ Email verified! Redirecting...");
                } else {
                  setResendNotice(locale === 'pl' ? "⚠️ E-mail nie został jeszcze zweryfikowany. Kliknij w link w wiadomości." : "⚠️ Email not verified yet. Please click the link in your email.");
                }
              }}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black py-3.5 rounded-2xl transition shadow-lg cursor-pointer flex items-center justify-center gap-2 active:scale-95 text-xs sm:text-sm"
            >
              <span className="material-symbols-outlined text-base">refresh</span>
              <span>{locale === 'pl' ? '⚡ Odśwież Status (Sprawdziłem Link)' : '⚡ Check Verification Status'}</span>
            </button>

            <button
              type="button"
              onClick={async () => {
                const sent = await resendVerificationEmail();
                if (sent) {
                  setResendNotice(locale === 'pl' ? "📧 Ponownie wysłano e-mail weryfikacyjny z contact@gymghost.net!" : "📧 Verification email resent from contact@gymghost.net!");
                } else {
                  setResendNotice(locale === 'pl' ? "⚠️ Nie udało się wysłać ponownej wiadomości." : "⚠️ Could not resend verification email.");
                }
              }}
              className="w-full bg-slate-800 hover:bg-slate-700 text-slate-200 font-extrabold py-3 rounded-2xl transition border border-slate-700 cursor-pointer flex items-center justify-center gap-2 active:scale-95 text-xs"
            >
              <span className="material-symbols-outlined text-base">send</span>
              <span>{locale === 'pl' ? 'Wyślij Link Ponownie' : 'Resend Email Link'}</span>
            </button>

            <button
              type="button"
              onClick={handleSignOut}
              className="w-full bg-transparent hover:bg-rose-950/30 text-slate-400 hover:text-rose-400 font-bold py-2.5 rounded-xl transition cursor-pointer text-xs flex items-center justify-center gap-1.5"
            >
              <span className="material-symbols-outlined text-sm">logout</span>
              <span>{locale === 'pl' ? 'Wyloguj się' : 'Sign Out'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-[#0a101d] text-slate-100 min-h-screen font-sans antialiased">
      {syncError && <div className="sync-banner">{syncError}</div>}

      <AppErrorBoundary>
        {/* 1. Super Admin Portal View */}
        {effectiveProfile?.role === 'admin' && (
          <SuperAdminPortal adminUser={user} logout={handleSignOut} />
        )}

        {/* 2. Coach/Trainer View with Zero Lock-Conflict Security Guard */}
        {isTrainerRole && (
          effectiveProfile?.status === 'locked' ? (
            <div className="min-h-screen bg-slate-950 text-slate-400 flex flex-col items-center justify-center p-6 text-center">
              <div className="bg-slate-900 border-2 border-red-500/50 p-8 md:p-10 rounded-3xl max-w-md shadow-[0_0_50px_rgba(239,68,68,0.2)] space-y-5 animate-fadeIn">
                <span className="material-symbols-outlined text-6xl text-red-500 block mb-2">lock</span>
                <h2 className="text-2xl font-black text-white m-0 tracking-wide">{lang === 'pl' ? 'Konto Trenera Zostało Zablokowane' : 'Your Coach Account is Locked'}</h2>
                <p className="text-sm text-slate-300 leading-relaxed m-0 font-medium">
                  {lang === 'pl' ? 'Twój profil trenera został zablokowany przez Administratora Platformy. Proszę skontaktować się z administratorem systemu SaaS w celu przywrócenia dostępu do bazy zawodników.' : 'Your trainer profile has been frozen by the platform owner. Please contact your system administrator to reactivate your access and client rosters.'}
                </p>
                <div className="pt-3 border-t border-slate-800">
                  <button 
                    onClick={handleSignOut} 
                    className="w-full bg-slate-800 hover:bg-rose-600 text-slate-200 hover:text-white font-extrabold px-6 py-3.5 rounded-xl transition cursor-pointer shadow-lg active:scale-95 text-sm"
                  >
                    {t('common.signOut', 'Sign Out')} ➔
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <AdminPortal 
              trainerId={effectiveProfile?.id || user?.email?.toLowerCase().trim() || user?.uid || "trainer_987654"} 
              trainerEmail={user?.email?.toLowerCase().trim()}
              userProfile={effectiveProfile}
              branding={activeBranding}
              onSignOut={handleSignOut} 
            />
          )
        )}

        {/* 3. Trainee View (Default fallback for all athlete/client/user/trainee and unassigned roles) */}
        {!isSuperAdmin && !isTrainerRole && (
          <TraineePortal 
            user={user} 
            userProfile={effectiveProfile} 
            routines={routines}
            traineePlans={traineePlans}
            branding={activeBranding}
            onSignOut={handleSignOut} 
          />
        )}
      </AppErrorBoundary>
    </div>
  );
}
